import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Clock, Sun, Ticket, Hourglass, Lightbulb, Check, MessageCircle, ArrowUpRight, Landmark, Navigation, Car } from "lucide-react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { photoSrc } from "@/components/Reviews";
import { api, waLink } from "@/lib/api";
import { HeartButton } from "@/components/WishlistDock";

const ZONES = [
  { key: "all", label: "All sites" },
  { key: "puri", label: "Puri town" },
  { key: "around", label: "Around Puri" },
  { key: "bhubaneswar", label: "Bhubaneswar & beyond" },
  { key: "odisha", label: "Rest of Odisha" },
];

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30);
export const cabFare = (km) => (km <= 0 ? 0 : km <= 50 ? km * 75 : km * 25);
export const BUSINESS_ORIGIN = { lat: 19.79996, lng: 85.80102, label: "Random Rider, Baliapanda, Puri" };
export const directionsUrl = (p) => `https://www.google.com/maps/dir/?api=1&origin=${BUSINESS_ORIGIN.lat},${BUSINESS_ORIGIN.lng}&destination=${p.lat},${p.lng}&travelmode=driving`;
const mapEmbed = (p) => `https://maps.google.com/maps?q=${p.lat},${p.lng}&z=13&output=embed`;

function Fact({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex gap-3 bg-white rounded-xl border border-ink/10 p-3.5" data-testid={testId}>
      <span className="w-8 h-8 rounded-full bg-terra/10 text-terra flex items-center justify-center shrink-0"><Icon size={15} /></span>
      <div className="min-w-0">
        <p className="text-[10px] font-mono uppercase tracking-widest text-ink/50">{label}</p>
        <p className="text-sm text-ink font-medium leading-snug">{value || "—"}</p>
      </div>
    </div>
  );
}

function PlaceDialog({ place, onClose }) {
  if (!place) return null;
  const fare = cabFare(place.km_from_puri);
  const hasGeo = place.lat && place.lng;
  return (
    <Dialog open={!!place} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl bg-sand border-ink/10 max-h-[90vh] overflow-y-auto p-0" data-testid="place-detail-dialog">
        <div className="relative h-56 sm:h-64">
          <img src={photoSrc(place.image)} alt={place.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent" />
          <div className="absolute bottom-4 left-6 right-6 text-sand">
            <p className="font-mono text-[11px] uppercase tracking-widest text-gold">{place.tagline}</p>
            <DialogHeader><DialogTitle className="font-serif text-2xl sm:text-3xl text-sand text-left">{place.name}</DialogTitle></DialogHeader>
          </div>
        </div>
        <div className="p-6 space-y-5">
          <p className="text-sm text-ink/75 leading-relaxed" data-testid="place-detail-description">{place.description}</p>

          {place.history && (
            <div className="bg-white rounded-2xl border border-ink/10 p-5" data-testid="place-detail-history">
              <p className="text-[10px] font-mono uppercase tracking-widest text-terra flex items-center gap-1.5 mb-2"><Landmark size={12} /> History & legend</p>
              <p className="text-sm text-ink/80 leading-relaxed">{place.history}</p>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <Fact icon={MapPin} label="Distance" value={place.distance} testId="place-fact-distance" />
            <Fact icon={Clock} label="Timings" value={place.timings} testId="place-fact-timings" />
            <Fact icon={Sun} label="Best time to visit" value={place.best_time} testId="place-fact-best-time" />
            <Fact icon={Ticket} label="Entry fee" value={place.entry_fee} testId="place-fact-entry-fee" />
            <Fact icon={Hourglass} label="Ideal duration" value={place.ideal_duration} testId="place-fact-duration" />
            <Fact icon={Car} label="Approx. cab from Puri (one way)" testId="place-fact-cab-fare"
              value={place.km_from_puri > 1 ? `₹${fare.toLocaleString("en-IN")} · ${place.km_from_puri} km × ₹${place.km_from_puri <= 50 ? 75 : 25}/km` : "Walkable / auto-rickshaw ₹50–100"} />
          </div>

          {hasGeo && (
            <div data-testid="place-detail-map">
              <p className="text-[10px] font-mono uppercase tracking-widest text-ink/50 mb-2">On the map</p>
              <div className="rounded-2xl overflow-hidden border border-ink/10 h-52 sm:h-60 bg-white">
                <iframe title={`${place.name} map`} src={mapEmbed(place)} className="w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen data-testid="place-map-iframe" />
              </div>
              <a href={directionsUrl(place)} target="_blank" rel="noreferrer" data-testid="place-directions-button"
                className="mt-3 flex items-center justify-center gap-2 w-full bg-ink text-sand text-sm font-semibold py-3 rounded-full hover:bg-terra transition-colors">
                <Navigation size={15} /> Get directions from Puri
              </a>
            </div>
          )}

          {place.highlights?.length > 0 && (
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-ink/50 mb-2">Don't miss</p>
              <ul className="grid sm:grid-cols-2 gap-2">
                {place.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-1.5 text-sm text-ink/80"><Check size={14} className="text-terra shrink-0 mt-0.5" /> {h}</li>
                ))}
              </ul>
            </div>
          )}
          {place.tips && (
            <div className="flex gap-3 bg-gold/15 border border-gold/40 rounded-xl p-4" data-testid="place-detail-tips">
              <Lightbulb size={18} className="text-terra shrink-0 mt-0.5" />
              <p className="text-sm text-ink/80 leading-relaxed"><span className="font-semibold">Local tip: </span>{place.tips}</p>
            </div>
          )}
          <Link to={`/places/${place.slug}`} data-testid="place-detail-page-link" className="flex items-center justify-center gap-2 w-full border border-ink/15 text-ink text-sm font-semibold py-3 rounded-full hover:bg-ink hover:text-sand transition-colors">
            <ArrowUpRight size={15} /> Open full guide & share
          </Link>
          <a href={waLink(`Hi Random Rider! I want to include ${place.name} in my Puri plan. Can you help with transport and timing?`)}
            target="_blank" rel="noreferrer" data-testid="place-detail-whatsapp-button"
            className="flex items-center justify-center gap-2 w-full bg-[#25D366] text-white text-sm font-semibold py-3 rounded-full hover:brightness-110 transition-all">
            <MessageCircle size={15} /> Plan a visit to {place.name}
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Places() {
  const [places, setPlaces] = useState([]);
  const [zone, setZone] = useState("all");
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/places").then((r) => setPlaces(r.data)).catch(() => {});
  }, []);

  const shown = zone === "all" ? places : places.filter((p) => p.zone === zone);

  return (
    <section id="places" className="py-20 lg:py-28 bg-surface" data-testid="places-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="max-w-2xl mb-10">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-terra mb-3">03 · Places to Visit</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ink">
            Every site, <span className="italic text-terra">every detail</span> — plan it yourself
          </h2>
          <p className="mt-4 text-ink/70 leading-relaxed">
            History, timings, entry fees, Google Maps directions and approximate cab fares from Puri for {places.length} must-see sites. Tap any card for the full guide.
          </p>
        </motion.div>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="places-zone-filter">
          {ZONES.map((z) => (
            <button key={z.key} onClick={() => setZone(z.key)} data-testid={`places-zone-${z.key}`}
              className={`text-xs font-semibold px-4 py-2 rounded-full transition-colors ${zone === z.key ? "bg-ink text-sand" : "bg-white border border-ink/15 text-ink/60 hover:text-ink"}`}>
              {z.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {shown.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: (i % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }} whileHover={{ y: -6 }}
              className="group relative bg-white rounded-2xl overflow-hidden border border-ink/10 hover:shadow-xl hover:shadow-ink/10 transition-shadow duration-500 flex flex-col">
              <HeartButton item={{ id: `place-${p.id}`, type: "place", name: p.name, slug: p.slug, image: p.image, meta: p.distance }} className="absolute top-3 right-3 z-10" />
              <button type="button" onClick={() => setActive(p)} data-testid={`place-card-${slugify(p.name)}`} className="text-left flex-1">
                <div className="relative h-44 overflow-hidden">
                  <img src={photoSrc(p.image)} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <span className="absolute top-3 left-3 bg-sand/95 text-ink text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full flex items-center gap-1">
                    <MapPin size={10} /> {p.distance.split("(")[0].replace("from Random Rider, Puri", "").replace("from Puri", "").trim()}
                  </span>
                </div>
                <div className="p-5 pb-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-terra">{p.tagline}</p>
                  <h3 className="font-serif text-lg font-bold text-ink leading-snug mt-1">{p.name}</h3>
                  <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-ink/60">
                    <span className="flex items-center gap-1"><Clock size={11} /> {p.ideal_duration}</span>
                    <span className="flex items-center gap-1"><Ticket size={11} /> {p.entry_fee.split("·")[0].trim()}</span>
                    {p.km_from_puri > 1 && <span className="flex items-center gap-1" data-testid={`place-cab-${slugify(p.name)}`}><Car size={11} /> ~₹{cabFare(p.km_from_puri).toLocaleString("en-IN")} cab</span>}
                  </div>
                </div>
              </button>
              <div className="mx-5 mb-4 flex gap-2">
                <Link to={`/places/${p.slug}`} data-testid={`place-page-${slugify(p.name)}`}
                  className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-sand bg-ink rounded-full py-2 hover:bg-terra transition-colors">
                  Full guide
                </Link>
                {p.lat && p.lng ? (
                  <a href={directionsUrl(p)} target="_blank" rel="noreferrer" data-testid={`place-directions-${slugify(p.name)}`}
                    className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-ink/70 border border-ink/15 rounded-full py-2 hover:bg-ink hover:text-sand hover:border-ink transition-colors">
                    <Navigation size={12} /> Directions
                  </a>
                ) : null}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      <PlaceDialog place={active} onClose={() => setActive(null)} />
    </section>
  );
}
